/**
 * MarketConditionCategorySection Component
 *
 * SPECIFIC section for managing Market Condition Categories.
 * Self-contained with own state management, data loading, and sync subscriptions.
 *
 * Features:
 * - Full CRUD: create, edit, archive, restore
 * - List display with color swatches and "used in" counts
 * - Archive toggle (only shown if archived categories exist)
 * - Cross-tab sync subscription
 * - Loading, error, and empty states
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Eye, EyeOff, AlertTriangle, RefreshCw } from 'lucide-react';

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  EmptyState,
  useDisclosure,
} from '@enfomentis/ui';
import { marketConditionCategoryService } from '@/services/MarketConditionCategoryService';
import { criteriaService } from '@/services/CriteriaService';
import { confluenceService } from '@/services/ConfluenceService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { Confluence } from '@/types/schema/Confluence';

import { CategoryListItem } from './CategoryListItem';
import { MarketConditionUsedInPopover } from './MarketConditionUsedInPopover';
import { MarketConditionCategoryModal } from './MarketConditionCategoryModal';
import { ArchiveCategoryWarningModal } from './ArchiveCategoryWarningModal';

export const MarketConditionCategorySection: React.FC = () => {
  // Data state
  const [categories, setCategories] = useState<MarketConditionCategory[]>([]);
  const [criteria, setCriteria] = useState<PlaybookCriteriaItem[]>([]);
  const [confluences, setConfluences] = useState<Confluence[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // UI state
  const [showArchived, setShowArchived] = useState(false);

  // Modal state
  const modalDisclosure = useDisclosure();
  const [editingCategory, setEditingCategory] = useState<MarketConditionCategory | undefined>();
  const [archivingCategory, setArchivingCategory] = useState<MarketConditionCategory | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const [allCategories, allCriteria, allConfluences] = await Promise.all([
        marketConditionCategoryService.getAll(),
        criteriaService.getAll(),
        confluenceService.getAll(),
      ]);
      setCategories(allCategories);
      setCriteria(allCriteria);
      setConfluences(allConfluences);
    } catch (error) {
      console.error('Failed to load category data:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscribe to cross-tab sync for category changes
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('market_condition_categories', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Subscribe to criteria changes (for "used in" count updates)
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('playbook_criteria_items', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Subscribe to confluence changes (for "used in" count updates)
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('confluences', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Build usedInMap: categoryId -> criteriaNames[]
  const usedInMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const criteriaItem of criteria) {
      if (criteriaItem.marketConditionCategoryId) {
        const names = map.get(criteriaItem.marketConditionCategoryId) || [];
        names.push(criteriaItem.name);
        map.set(criteriaItem.marketConditionCategoryId, names);
      }
    }
    return map;
  }, [criteria]);

  // Build confluenceUsedInMap: categoryId -> confluenceNames[]
  const confluenceUsedInMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const confluence of confluences) {
      if (confluence.marketConditionCategoryId) {
        const names = map.get(confluence.marketConditionCategoryId) || [];
        names.push(confluence.name);
        map.set(confluence.marketConditionCategoryId, names);
      }
    }
    // Sort each array alphabetically (case-insensitive)
    for (const [key, names] of map) {
      map.set(key, names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
    }
    return map;
  }, [confluences]);

  // Check if there are any archived categories
  const hasArchivedCategories = useMemo(() => {
    return categories.some(c => c.status === 'archived');
  }, [categories]);

  // Filter and sort categories
  const sortedCategories = useMemo(() => {
    // Step 1: Apply archived filter
    const filtered = showArchived
      ? categories
      : categories.filter(c => c.status !== 'archived');

    // Step 2: Sort - active first (alphabetical), then archived (alphabetical)
    return [...filtered].sort((a, b) => {
      // Active items always first
      if (a.status === 'archived' && b.status !== 'archived') return 1;
      if (a.status !== 'archived' && b.status === 'archived') return -1;

      // Then alphabetical by name (case-insensitive)
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [categories, showArchived]);

  // Handlers
  const handleAddClick = () => {
    setEditingCategory(undefined);
    modalDisclosure.onOpen();
  };

  const handleEditClick = (category: MarketConditionCategory) => {
    setEditingCategory(category);
    modalDisclosure.onOpen();
  };

  const handleSave = async (data: { name: string; description?: string; color: string }) => {
    setIsSaving(true);
    try {
      if (editingCategory) {
        // Update existing category
        await marketConditionCategoryService.update(editingCategory.id, data);
        syncCoordinatorService.notifyChange('market_condition_categories', editingCategory.id);
      } else {
        // Create new category with next order value
        // Use fallback of 0 for any undefined order values to prevent NaN
        const nextOrder = categories.length > 0
          ? Math.max(...categories.map(c => c.order ?? 0)) + 1
          : 0;
        const newCategory = await marketConditionCategoryService.add({
          ...data,
          order: nextOrder,
        });
        syncCoordinatorService.notifyChange('market_condition_categories', newCategory.id);
      }
      await loadData();
      modalDisclosure.onClose();
    } catch (error) {
      console.error('Failed to save category:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveClick = (category: MarketConditionCategory) => {
    setArchivingCategory(category);
  };

  const handleArchiveConfirm = async () => {
    if (!archivingCategory) return;
    try {
      await marketConditionCategoryService.update(archivingCategory.id, { status: 'archived' });
      syncCoordinatorService.notifyChange('market_condition_categories', archivingCategory.id);
      await loadData();
    } catch (error) {
      console.error('Failed to archive category:', error);
    } finally {
      setArchivingCategory(undefined);
    }
  };

  const handleArchiveCancel = () => {
    setArchivingCategory(undefined);
  };

  const handleRestore = async (category: MarketConditionCategory) => {
    try {
      await marketConditionCategoryService.update(category.id, { status: 'active' });
      syncCoordinatorService.notifyChange('market_condition_categories', category.id);
      await loadData();
    } catch (error) {
      console.error('Failed to restore category:', error);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card radius="none" shadow="none">
        <CardHeader className="py-3 border-b border-default-200">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-text-primary">Market Condition Categories</span>
            <p className="text-xs text-text-secondary truncate" title="Optional groupings to organize criteria and confluences">
              Optional groupings to organize criteria and confluences
            </p>
          </div>
        </CardHeader>
        <CardBody className="px-4 py-4">
          <div className="text-center py-8">
            <p className="text-text-secondary">Loading categories...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Error state
  if (loadError) {
    return (
      <Card radius="none" shadow="none">
        <CardHeader className="py-3 border-b border-default-200">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-text-primary">Market Condition Categories</span>
            <p className="text-xs text-text-secondary truncate" title="Optional groupings to organize criteria and confluences">
              Optional groupings to organize criteria and confluences
            </p>
          </div>
        </CardHeader>
        <CardBody className="px-4 py-4">
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="flex items-center gap-2 text-danger-600">
              <AlertTriangle size={20} />
              <p className="text-sm">Failed to load categories</p>
            </div>
            <Button
              size="sm"
              variant="flat"
              startContent={<RefreshCw size={16} />}
              onPress={loadData}
            >
              Retry
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Empty state
  if (categories.length === 0) {
    return (
      <>
        <Card radius="none" shadow="none">
          <CardHeader className="py-3 border-b border-default-200  bg-default-50">
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold text-text-primary">Market Condition Categories</span>
              <p className="text-xs text-text-secondary truncate" title="Optional groupings to organize criteria and confluences">
                Optional groupings to organize criteria and confluences
              </p>
            </div>
          </CardHeader>
          <CardBody className="px-4 py-4">
            <EmptyState
              title="No categories yet"
              description="Market condition categories are optional groupings to organize your criteria and confluences. Create categories to add structure to your playbook conditions."
              action={
                <Button
                  variant="light"
                  startContent={<Plus size={16} />}
                  onPress={handleAddClick}
                >
                  Add Category
                </Button>
              }
            />
          </CardBody>
        </Card>
        <MarketConditionCategoryModal
          isOpen={modalDisclosure.isOpen}
          onClose={modalDisclosure.onClose}
          category={editingCategory}
          onSave={handleSave}
          isSaving={isSaving}
        />
      </>
    );
  }

  // Main content
  return (
    <>
      <Card radius="none" shadow="none">
        {/* Header with controls */}
        <CardHeader className="py-3 border-b border-default-200  bg-default-50">
          <div className="flex items-center justify-between w-full gap-3">
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <span className="font-semibold text-text-primary">Market Condition Categories</span>
              <p className="text-xs text-text-secondary truncate" title="Optional groupings to organize criteria and confluences">
                Optional groupings to organize criteria and confluences
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Archived Toggle - only show if archived exist */}
              {hasArchivedCategories && (
                <Button
                  size="sm"
                  variant="flat"
                  startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                  onPress={() => setShowArchived(!showArchived)}
                  className={`bg-default-100 shrink-0 ${
                    showArchived ? 'text-text-primary' : 'text-text-tertiary'
                  }`}
                >
                  Archived
                </Button>
              )}

              {/* Add Button */}
              <Button
                variant="light"
                size="sm"
                startContent={<Plus size={16} />}
                onPress={handleAddClick}
                className="shrink-0"
              >
                Add
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardBody className="px-0 py-4">
          {/* Category List */}
          {sortedCategories.length > 0 && (
            <div className="space-y-2">
              {sortedCategories.map((category) => (
                <Card
                  key={category.id}
                  radius="sm"
                  shadow="none"
                  className="border border-default-200"
                >
                  <CardBody className="p-0">
                    <CategoryListItem
                      category={category}
                      usedInContent={
                        <MarketConditionUsedInPopover
                          criteriaNames={usedInMap.get(category.id) || []}
                          confluenceNames={confluenceUsedInMap.get(category.id) || []}
                        />
                      }
                      onEdit={() => handleEditClick(category)}
                      onArchiveClick={() => handleArchiveClick(category)}
                      onRestore={() => handleRestore(category)}
                    />
                  </CardBody>
                </Card>
              ))}
            </div>
          )}

          {/* No results from archived filter */}
          {sortedCategories.length === 0 && categories.length > 0 && (
            <EmptyState
              title="No active categories"
              description="All categories are archived. Enable 'Archived' to view them."
            />
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <MarketConditionCategoryModal
        isOpen={modalDisclosure.isOpen}
        onClose={modalDisclosure.onClose}
        category={editingCategory}
        onSave={handleSave}
        isSaving={isSaving}
      />

      {/* Archive Warning Modal */}
      <ArchiveCategoryWarningModal
        isOpen={!!archivingCategory}
        category={archivingCategory}
        usageGroups={[
          {
            label: 'Criteria using this category:',
            singularLabel: 'criterion',
            pluralLabel: 'criteria',
            names: archivingCategory ? usedInMap.get(archivingCategory.id) || [] : [],
          },
          {
            label: 'Confluences using this category:',
            singularLabel: 'confluence',
            pluralLabel: 'confluences',
            names: archivingCategory ? confluenceUsedInMap.get(archivingCategory.id) || [] : [],
          },
        ]}
        onConfirm={handleArchiveConfirm}
        onCancel={handleArchiveCancel}
      />
    </>
  );
};
